/**
 * Chat comprador–vendedor (Supabase: `conversations` + `messages`).
 */

export async function ensureConversation(supabase, { sellerId, listingId }) {
  if (!sellerId) return { conversationId: null, error: 'missing_seller' };
  const { data, error } = await supabase.rpc('ensure_conversation', {
    p_seller_id: sellerId,
    p_listing_id: listingId,
  });
  if (error) {
    console.error('[chatService] ensure_conversation:', error);
    return { conversationId: null, error: error.message || 'rpc_error' };
  }
  const conversationId = typeof data === 'string' ? data : data;
  return { conversationId, error: null };
}

export async function fetchMessages(supabase, conversationId) {
  const { data, error } = await supabase
    .from('messages')
    .select('id, sender_id, content, created_at, moderation_status')
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true });
  if (error) {
    console.error('[chatService] fetchMessages:', error);
    return { messages: [], error: error.message };
  }
  return { messages: data ?? [], error: null };
}

export async function sendMessage(supabase, { conversationId, senderId, content }) {
  const text = String(content || '').trim();
  if (!text) return { error: 'empty' };
  const { error } = await supabase.rpc('send_chat_message', {
    p_conversation_id: conversationId,
    p_content: text,
  });
  if (error) {
    console.error('[chatService] sendMessage:', error);
    const msg = String(error.message || '');
    if (msg.includes('urls_not_allowed')) {
      return { error: 'No incluyas enlaces en el chat; compártelos fuera de esta conversación.' };
    }
    if (msg.includes('html_not_allowed') || msg.includes('attachments_not_allowed')) {
      return { error: 'Solo texto y emoticonos; sin archivos ni imágenes en el chat.' };
    }
    if (msg.includes('message_too_long')) {
      return { error: 'Mensaje demasiado largo (máximo 700 caracteres).' };
    }
    if (msg.includes('empty_message')) {
      return { error: 'empty' };
    }
    return { error: error.message };
  }
  return { error: null };
}

export async function fetchConversationSummaries(supabase, userId) {
  const { data: convs, error } = await supabase
    .from('conversations')
    .select('id, buyer_id, seller_id, listing_id, updated_at, buyer_deleted_at, seller_deleted_at')
    .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
    .order('updated_at', { ascending: false });
  if (error) {
    console.error('[chatService] fetchConversationSummaries:', error);
    return [];
  }
  const rows = (convs ?? []).filter(c => {
    if (c.buyer_id === userId) return c.buyer_deleted_at == null;
    if (c.seller_id === userId) return c.seller_deleted_at == null;
    return true;
  });
  const ids = [...new Set(rows.flatMap(c => [c.buyer_id, c.seller_id]))];
  let nameById = {};
  let avatarById = {};
  if (ids.length) {
    const { data: profs } = await supabase.from('profiles').select('id, username, avatar_url').in('id', ids);
    nameById = Object.fromEntries((profs ?? []).map(p => [p.id, p.username]));
    avatarById = Object.fromEntries((profs ?? []).map(p => [p.id, p.avatar_url ?? null]));
  }
  const out = [];
  for (const c of rows) {
    const otherId = c.buyer_id === userId ? c.seller_id : c.buyer_id;
    const role = c.buyer_id === userId ? 'sent' : c.seller_id === userId ? 'received' : 'unknown';
    const { data: last } = await supabase
      .from('messages')
      .select('content, moderation_status')
      .eq('conversation_id', c.id)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    const hidden = String(last?.moderation_status || 'approved') === 'hidden';
    out.push({
      id: c.id,
      otherUsername: nameById[otherId] || 'Usuario',
      otherAvatarUrl: avatarById[otherId] || null,
      preview: hidden ? 'Mensaje ocultado por moderación' : last?.content ? String(last.content) : '',
      updated_at: c.updated_at,
      listing_id: c.listing_id,
      role,
    });
  }
  return out;
}
